import React, { useEffect, useState } from "react";
import { Modal, Input, Form, InputNumber, Select, Tag, Button, Space, Tooltip } from "antd";
import { GoldOutlined, SyncOutlined } from "@ant-design/icons";
import { message } from "antd";
import { createScheme, updateScheme } from "../api";
import { fetchHallmarkGoldRate, invalidateGoldPriceCache } from "../../../services/goldPriceService";
import { usePermissions } from "../../../hooks/usePermissions";

const { Option } = Select;

const AddSchemeModel = ({ visible, onClose, schemeToEdit }) => {
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const { canViewField, canEditField } = usePermissions();

  // Two-tier show/edit booleans
  const canFieldNameShow = canEditField('scheme', 'name') || canViewField('scheme', 'name');
  const canFieldNameEdit = canEditField('scheme', 'name');
  const canFieldCodeShow = canEditField('scheme', 'code') || canViewField('scheme', 'code');
  const canFieldCodeEdit = canEditField('scheme', 'code');
  const canFieldRateShow = canEditField('scheme', 'ratePerGram') || canViewField('scheme', 'ratePerGram');
  const canFieldRateEdit = canEditField('scheme', 'ratePerGram');
  const canFieldStatusShow = canEditField('scheme', 'status') || canViewField('scheme', 'status');
  const canFieldStatusEdit = canEditField('scheme', 'status');

  // Live gold rate
  const [liveRate, setLiveRate] = useState(null);   // { ratePerGram, lastUpdate }
  const [rateLoading, setRateLoading] = useState(false);

  const loadLiveRate = async (silent = false) => {
    if (!silent) setRateLoading(true);
    const rate = await fetchHallmarkGoldRate();
    if (rate) setLiveRate(rate);
    setRateLoading(false);
    return rate;
  };

  const handleRefreshRate = async () => {
    invalidateGoldPriceCache();
    const rate = await loadLiveRate();
    if (rate) {
      form.setFieldsValue({ rate: rate.ratePerGram });
      messageApi.success(`Live rate updated: ₹${rate.ratePerGram}/gm`);
    } else {
      messageApi.warning('Could not fetch live rate');
    }
  };

  // Fetch live rate whenever modal opens
  useEffect(() => {
    if (!visible) return;
    loadLiveRate(true).then((rate) => {
      if (rate && !schemeToEdit) {
        // Pre-fill only on Add; Edit keeps the saved rate
        form.setFieldsValue({ rate: rate.ratePerGram });
      }
    });
  }, [visible]);

  // Populate form when editing
  useEffect(() => {
    if (schemeToEdit) {
      form.setFieldsValue({
        name: schemeToEdit.name,
        code: schemeToEdit.code,
        rate: schemeToEdit.ratePerGram,
        status: schemeToEdit.status,
        type: schemeToEdit.type,
      });
    } else {
      form.resetFields();
    }
  }, [schemeToEdit, form]);

  // To Create
  const handleSubmit = () => {
    form.validateFields().then(async (values) => {
      console.log("Submitted values:", values);
      const bodyData = {
        name: values.name,
        code: values.code,
        ratePerGram: values.rate,
        status: values.status,
        type: values.type,
      };

      console.log(" ## schemeToEdit : " + JSON.stringify(schemeToEdit));
      try {
        if (schemeToEdit) {
          const response = await updateScheme({
            id: schemeToEdit._id,
            bodyData: bodyData,
          });
          messageApi.success(response.data.message);
        } else {
          const response = await createScheme({ bodyData: bodyData });
          messageApi.success(response.data.message);
          form.resetFields();
        }
        onClose(true);
      } catch (error) {
        console.error("Error saving scheme:", error);
        messageApi.error("Failed to save scheme");
      }
    });
  };
 // Need this contextHolder to put over here to show message else it will not.
  return (
    <>
    {contextHolder}
    <Modal
      title={schemeToEdit ? "Edit Scheme" : "Add Scheme"}
      open={visible}
      onOk={handleSubmit}
      onCancel={onClose}
      okText={schemeToEdit ? "Update" : "Add"}
    >
      <Form layout="vertical" form={form}>
        {canFieldNameShow && (
        <Form.Item
          label="Scheme Name"
          name="name"
          rules={[{ required: true, message: "Please enter scheme name" }]}
        >
          <Input disabled={!canFieldNameEdit} />
        </Form.Item>
        )}

        {canFieldCodeShow && (
        <Form.Item
          label="Scheme Code"
          name="code"
          rules={[{ required: true, message: "Please enter scheme code" }]}
        >
          <Input disabled={!canFieldCodeEdit} />
        </Form.Item>
        )}

        {canFieldRateShow && (
        <Form.Item
          label={
            <Space size={8}>
              <span>Rate Per Gram</span>
              {liveRate && (
                <Tooltip title={`Hallmark 916 / 22-carat — last updated ${liveRate.lastUpdate}`}>
                  <Tag
                    color="gold"
                    icon={<GoldOutlined />}
                    style={{ cursor: 'default', fontSize: 12, margin: 0 }}
                  >
                    Live: ₹{liveRate.ratePerGram.toLocaleString('en-IN')}/gm
                  </Tag>
                </Tooltip>
              )}
              <Tooltip title="Refresh live rate and apply">
                <Button
                  type="text"
                  size="small"
                  icon={<SyncOutlined spin={rateLoading} />}
                  onClick={handleRefreshRate}
                  loading={rateLoading}
                  style={{ padding: '0 4px', height: 20 }}
                />
              </Tooltip>
            </Space>
          }
          name="rate"
          rules={[{ required: true, message: "Please enter rate per gram" }]}
        >
          <InputNumber style={{ width: "100%" }} min={0} prefix="₹" precision={2} disabled={!canFieldRateEdit} />
        </Form.Item>
        )}

        {canFieldStatusShow && (
        <Form.Item
          label="Status"
          name="status"
          rules={[{ required: true, message: "Please select a status" }]}
        >
          <Select placeholder="Select status" disabled={!canFieldStatusEdit}>
            <Option value="active">Active</Option>
            <Option value="inactive">Inactive</Option>
            <Option value="closed">Closed</Option>
          </Select>
        </Form.Item>
        )}

        <Form.Item
          label="Type"
          name="type"
          rules={[{ required: true, message: "Please select a type" }]}
        >
          <Select placeholder="Select type">
            <Option value="Agent">Agent</Option>
            <Option value="Basic">Basic</Option>
          </Select>
        </Form.Item>

      </Form>
    </Modal>
    </>
  );
};

export default AddSchemeModel;